import React, { ReactNode, useState } from 'react'
import { ReactComponent as NoDataImage } from 'assets/shared/NotFound.svg'
import { Table as RsuiteTable } from 'rsuite'
import { TableProps, RowDataType, RowKeyType } from 'rsuite-table'
import type { ColumnProps } from 'rsuite-table/lib/Column'
import classNames from 'classnames'
import {
  HeaderCell,
  Cell,
  StatusCell,
  RoleCell,
  ActionCell,
  CheckCell,
  CheckHeaderCell,
  CustomCell,
  DateTimeCell,
  VerifiedCell,
  QuotationStatusCell,
  PaymentModeCell,
  PaymentStatusCell,
  EscalationCell
} from './cells'
import { TableSkeleton } from '../../atoms/skeleton/Skeleton'
import TableFooter from './footer/TableFooter'
import './Table.scss'

const { Column: RsuiteColumn } = RsuiteTable

const MOBILE_BREAKPOINT = 768
const MOBILE_COLUMN_WIDTH = 150

const useIsMobile = () => {
  const [isMobile, setIsMobile] = React.useState(
    () =>
      typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT
  )

  React.useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`)
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches)
    mql.addEventListener('change', handler)
    return () => mql.removeEventListener('change', handler)
  }, [])

  return isMobile
}

const MobileContext = React.createContext(false)

const Column: React.FC<ColumnProps<RowDataType>> = (props) => {
  const isMobile = React.useContext(MobileContext)

  if (isMobile) {
    const { flexGrow: _fg, fixed: _fixed, width, ...rest } = props
    return <RsuiteColumn width={width ?? MOBILE_COLUMN_WIDTH} {...rest} />
  }

  return <RsuiteColumn {...props} />
}

export interface Pagination {
  page: number
  limit: number
}

const DEFAULT_PAGE_SIZE = 10

const EmptyState = () => (
  <div className="table-empty">
    <NoDataImage className="table-empty__image" />
    <p className="table-empty__text">No Data Found</p>
  </div>
)

function Table<R extends RowDataType<any>, K extends RowKeyType>(
  props: TableProps<R, K> & {
    paginated?: boolean
    pageSizeOptions?: number[]
    defaultPageSize?: number
    total?: number
    currentPage?: number
    onPageChange?: (page: number, pageSize: number) => void
    maxButtons?: number
    rowDisabledHandler?: (rowData: R, index: number) => boolean
    toolbar?: ReactNode
  }
): JSX.Element {
  const {
    data = [],
    children,
    loading = false,
    hover = true,
    fillHeight: _fillHeight,
    height,
    autoHeight,
    maxHeight,
    onRowClick,
    paginated = false,
    pageSizeOptions,
    defaultPageSize = DEFAULT_PAGE_SIZE,
    total = data.length,
    onPageChange,
    currentPage = 1,
    maxButtons = 3,
    rowClassName,
    rowDisabledHandler,
    toolbar,
    renderEmpty,
    ...rest
  } = props

  const initialPageSize =
    pageSizeOptions && pageSizeOptions.length > 0
      ? pageSizeOptions[0]
      : defaultPageSize

  const [pageSize, setPageSize] = useState(initialPageSize)

  const handlePageChangeInternal = (page: number) => {
    if (onPageChange) {
      onPageChange(page, pageSize)
    }
  }

  const handlePageSizeChangeInternal = (
    value: number | null,
    _: React.SyntheticEvent
  ) => {
    if (value !== null) {
      setPageSize(value)
      if (onPageChange) {
        onPageChange(1, value)
      }
    }
  }

  let heightProps
  if (autoHeight) {
    heightProps = { autoHeight: true as const, maxHeight }
  } else if (height) {
    heightProps = { height }
  } else {
    heightProps = { fillHeight: true as const }
  }

  const isMobile = useIsMobile()

  const columnCount =
    typeof children !== 'function'
      ? React.Children.toArray(children as React.ReactNode).filter(
          React.isValidElement
        ).length
      : 5

  const transformedChildren = isMobile
    ? (React.Children.map(children as React.ReactNode, (child) => {
        if (!React.isValidElement(child)) return child
        const childProps = child.props as ColumnProps<RowDataType>
        const { flexGrow: _fg, fixed: _fixed, width, ...colRest } = childProps
        return (
          <RsuiteColumn width={width ?? MOBILE_COLUMN_WIDTH} {...colRest} />
        )
      }) as React.ReactNode)
    : children

  return (
    <MobileContext.Provider value={isMobile}>
      <div
        className={classNames('table-container', {
          'table-container--mobile': isMobile
        })}
      >
        {toolbar && <div className="table-toolbar">{toolbar}</div>}
        <div className="table-wrapper">
          {loading ? (
            <TableSkeleton
              rows={pageSize ?? defaultPageSize}
              columns={columnCount || 5}
            />
          ) : (
            <RsuiteTable
              loading={false}
              {...heightProps}
              wordWrap={isMobile ? undefined : 'break-word'}
              hover={hover}
              fillHeight={!autoHeight && !height}
              data={data}
              onRowClick={onRowClick}
              renderEmpty={renderEmpty ?? (() => <EmptyState />)}
              rowClassName={(rowData, index) => {
                let customRowClass = rowClassName
                if (typeof rowClassName === 'function' && rowData) {
                  customRowClass = rowClassName?.(rowData, index)
                }
                return classNames(
                  rowDisabledHandler?.(rowData, index)
                    ? 'disabled-table-row'
                    : '',
                  customRowClass,
                  {
                    'clickable-row': !!onRowClick
                  }
                )
              }}
              {...rest}
            >
              {transformedChildren}
            </RsuiteTable>
          )}
        </div>
        {paginated && (
          <TableFooter
            total={total}
            pageSize={pageSize}
            currentPage={currentPage}
            pageSizeOptions={pageSizeOptions}
            onPageChange={handlePageChangeInternal}
            onPageSizeChange={handlePageSizeChangeInternal}
            maxButtons={maxButtons}
          >
            {`${(currentPage - 1) * pageSize + 1}-${Math.min(
              currentPage * pageSize,
              total
            )} of ${total}`}
          </TableFooter>
        )}
      </div>
    </MobileContext.Provider>
  )
}

Table.Column = Column
Table.Cell = Cell
Table.HeaderCell = HeaderCell
Table.StatusCell = StatusCell
Table.RoleCell = RoleCell
Table.ActionCell = ActionCell
Table.CheckCell = CheckCell
Table.CheckHeaderCell = CheckHeaderCell
Table.CustomCell = CustomCell
Table.DateTimeCell = DateTimeCell
Table.VerifiedCell = VerifiedCell
Table.QuotationStatusCell = QuotationStatusCell
Table.PaymentModeCell = PaymentModeCell
Table.PaymentStatusCell = PaymentStatusCell
Table.EscalationCell = EscalationCell
Table.DEFAULT_PAGE_SIZE = DEFAULT_PAGE_SIZE

export default Table
